/**
 * MobileNavBar Component
 *
 * A beautiful bottom navigation bar for mobile devices.
 * This replaces the sidebar on phones for easier one-handed use.
 *
 * INSTALLATION:
 * 1. Copy this file to: src/components/MobileNavBar.jsx
 * 2. Import it in App.jsx: import MobileNavBar from './components/MobileNavBar';
 * 3. Add it to your layout: {isMobile && <MobileNavBar user={user} />}
 */

import { Home, Bookmark, User, Rss } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const MobileNavBar = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Define your navigation items here
  // Change these to match your app's routes!
  const navItems = [
    {
      path: "/",
      icon: Home,
      label: "Build",
      requiresAuth: false,
    },
    {
      path: "/feed",
      icon: Rss,
      label: "Feed",
      requiresAuth: false,
    },
    {
      path: user ? "/library" : "/profile",
      icon: user ? Bookmark : User,
      label: user ? "Library" : "Login",
      requiresAuth: false,
    },
  ];

  // Check if a nav item is currently active
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="mobile-nav md:hidden">
      {/* Inner container with flex layout */}
      <div className="flex items-center justify-around px-2 py-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`mobile-nav-item touch-active ${active ? "active" : ""}`}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
            >
              {/* Icon */}
              <Icon
                size={22}
                className={`mobile-nav-item-icon transition-colors ${
                  active
                    ? "text-[hsl(var(--primary-channels))] dark:text-[hsl(var(--primary-channels))]"
                    : "text-slate-500 dark:text-slate-200"
                }`}
                strokeWidth={active ? 2.5 : 2}
              />

              {/* Label */}
              <span
                className={`mobile-nav-item-label transition-colors ${
                  active
                    ? "text-[hsl(var(--primary-channels))] dark:text-[hsl(var(--primary-channels))]"
                    : "text-slate-500 dark:text-slate-200"
                }`}
              >
                {item.label}
              </span>

              {/* Active indicator dot */}
              {active && (
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[hsl(var(--primary-channels))] rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileNavBar;
